import React, { useState } from 'react'
import { CircularProgress } from '@mui/material'

type Props = {
  onSave: (name: string) => Promise<void>
  onClose: () => void
}

export function SaveAsImageModal({ onSave, onClose }: Props) {
  const [name, setName] = useState('')
  const [saving, setSaving] = useState(false)

  const handleSave = async () => {
    if (!name.trim()) return
    setSaving(true)
    try {
      await onSave(name.trim())
      onClose()
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 flex flex-col gap-5">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Save as Ad Image</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Uploads the image to your Facebook ad library for use in future creatives
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors ml-4 flex-shrink-0"
          >
            <i className="fa fa-times text-lg" />
          </button>
        </div>

        <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-amber-50 border border-amber-200">
          <i className="fa fa-triangle-exclamation text-amber-500 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-amber-800 leading-snug">
            This saves the image only — no ad copy will be attached. To save with copy, use{' '}
            <strong>Save as Creative</strong> instead.
          </p>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-sm font-semibold text-gray-700">Name *</label>
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="e.g. Summer Sale — AI Generated v1"
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
        </div>

        <div className="flex gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!name.trim() || saving}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-purple-600 text-purple-600 text-sm font-semibold hover:bg-purple-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <i className="fa fa-image" />
            )}
            Save as Ad Image
          </button>
        </div>
      </div>
    </div>
  )
}
